import React from "react";
import './ContactForm.css';
import './main.css';

type TranslationShape = {
    contact: string;
    send: string;
    sending: string;
    name: string;
    email: string;
    message: string;
    success: string;
    submitError: string;
    errors: {
        nameRequired: string;
        emailRequired: string;
        emailInvalid: string;
        messageRequired: string;
        messageShort: string;
    };
};

type FormValues = {
    name: string;
    email: string;
    message: string;
    website: string;
};

type FormErrors = Partial<Record<keyof FormValues, string>>;

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function ContactForm({
                                t,
                                onSubmitSuccess,
                            }: {
    t: TranslationShape;
    onSubmitSuccess?: () => void;
}) {
    const [values, setValues] = React.useState<FormValues>({
        name: "",
        email: "",
        message: "",
        website: "",
    });

    const [errors, setErrors] = React.useState<FormErrors>({});
    const [touched, setTouched] = React.useState<
        Partial<Record<keyof FormValues, boolean>>
    >({});
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const [submitError, setSubmitError] = React.useState("");
    const openedAtRef = React.useRef(Date.now());
    const validate = (v: FormValues): FormErrors => {
        const nextErrors: FormErrors = {};

        if (!v.name.trim()) {
            nextErrors.name = t.errors.nameRequired;
        }

        if (!v.email.trim()) {
            nextErrors.email = t.errors.emailRequired;
        } else if (!emailRegex.test(v.email.trim())) {
            nextErrors.email = t.errors.emailInvalid;
        }

        if (!v.message.trim()) {
            nextErrors.message = t.errors.messageRequired;
        } else if (v.message.trim().length < 10) {
            nextErrors.message = t.errors.messageShort;
        }

        return nextErrors;
    };

    const handleChange =
        (field: keyof FormValues) =>
            (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                const nextValues = { ...values, [field]: e.target.value };
                setValues(nextValues);

                if (touched[field]) {
                    setErrors(validate(nextValues));
                }

                if (submitError) setSubmitError("");
            };

    const handleBlur = (field: keyof FormValues) => () => {
        setTouched((prev) => ({ ...prev, [field]: true }));
        setErrors(validate(values));
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (lastSubmitAt && Date.now() - lastSubmitAt < 15000) {
            setSubmitError(t.submitError);
            return;
        }
        if (values.website.trim()) {
            return;
        }
        const secondsSinceOpen = (Date.now() - openedAtRef.current) / 1000;

        if (secondsSinceOpen < 3) {
            setSubmitError(t.submitError);
            return;
        }
        const allTouched = {
            name: true,
            email: true,
            message: true,
        };
        setTouched(allTouched);

        const nextErrors = validate(values);
        setErrors(nextErrors);

        if (Object.keys(nextErrors).length > 0) return;

        setIsSubmitting(true);
        setSubmitError("");

        try {
            const response = await fetch("https://api.bgc.ugarstudio.com/contact", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json",
                },
                body: JSON.stringify({
                    name: values.name.trim(),
                    email: values.email.trim(),
                    message: values.message.trim(),
                    source: window.location.href,
                    website: values.website.trim(),
                    formOpenedAt: openedAtRef.current,
                }),
            });

            const contentType = response.headers.get("content-type") || "";

            if (contentType.includes("application/json")) {
                await response.json();
            } else {
                await response.text();
            }

            if (!response.ok) {
                throw new Error(`Request failed with status ${response.status}`);
            }

            setValues({
                name: "",
                email: "",
                message: "",
                website: "",
            });
            setErrors({});
            setTouched({});
            setLastSubmitAt(Date.now());

            window.setTimeout(() => {
                onSubmitSuccess?.();
            }, 900);
        } catch (error) {
            console.error("Contact form submit failed:", error);
            setSubmitError(t.submitError);
        } finally {
            setIsSubmitting(false);
        }
    };
    const [lastSubmitAt, setLastSubmitAt] = React.useState<number | null>(null);
    const showError = (field: keyof FormValues) =>
        Boolean(touched[field] && errors[field]);

    return (
        <form className="contact-form" noValidate onSubmit={handleSubmit}>
            <div className={`field ${showError("name") ? "field-error" : ""}`}>
                <input
                    id="contact-name"
                    name="name"
                    value={values.name}
                    onChange={handleChange("name")}
                    onBlur={handleBlur("name")}
                    aria-invalid={showError("name")}
                    aria-describedby={showError("name") ? "contact-name-error" : undefined}
                    autoComplete="name"
                    placeholder={t.name}
                />
                {showError("name") && (
                    <div id="contact-name-error" className="field-error-text">
                        {errors.name}
                    </div>
                )}
            </div>

            <div className={`field ${showError("email") ? "field-error" : ""}`}>
                <input
                    id="contact-email"
                    name="email"
                    type="email"
                    value={values.email}
                    onChange={handleChange("email")}
                    onBlur={handleBlur("email")}
                    aria-invalid={showError("email")}
                    aria-describedby={showError("email") ? "contact-email-error" : undefined}
                    autoComplete="email"
                    placeholder={t.email}
                />
                {showError("email") && (
                    <div id="contact-email-error" className="field-error-text">
                        {errors.email}
                    </div>
                )}
            </div>

            <div className={`field ${showError("message") ? "field-error" : ""}`}>
        <textarea
            id="contact-message"
            name="message"
            rows={5}
            value={values.message}
            onChange={handleChange("message")}
            onBlur={handleBlur("message")}
            aria-invalid={showError("message")}
            aria-describedby={
                showError("message") ? "contact-message-error" : undefined
            }
            placeholder={t.message}
        />
                {showError("message") && (
                    <div id="contact-message-error" className="field-error-text">
                        {errors.message}
                    </div>
                )}
            </div>
            {submitError ? (
                <div className="form-submit-error" role="alert" aria-live="assertive">
                    {submitError}
                </div>
            ) : null}
            <div className="hp-field" aria-hidden="true">
                <input
                    type="text"
                    name="website"
                    tabIndex={-1}
                    autoComplete="off"
                    value={values.website}
                    onChange={handleChange("website")}
                    placeholder="Website"
                />
            </div>
            <button
                className="button button-primary submit-button"
                type="submit"
                disabled={isSubmitting}
            >
                {isSubmitting ? t.sending : t.send}
            </button>
        </form>
    );
}